// btw-front/src/pages/PartnerDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { StatCard } from "../components/ui/StatCard";
import { Input } from "../components/ui/Input";
import { EmptyState } from "../components/ui/EmptyState";
import { Button } from "../components/ui/Button";
import { usePartnerSession } from "../context/PartnerSessionContext";
import { list as listDeals } from "../api/partnerDeals";
import { featured as fetchFeaturedNotice } from "../api/notices";
import { SALES_STAGES, IN_PROGRESS_STAGES } from "../constants/dealStatus";
import {
  isReviewApproved,
  isReviewPending,
  effectiveStatus,
  statusFilterOrder,
} from "../constants/dealReview";
import { DealStageBadge } from "../components/partner/DealStageBadge";
import { formatQuarter } from "../lib/format";
import { useDealRegistrationGate } from "../lib/dealRegistrationGate";
import { useQueryState } from "../lib/useQueryState";

function formatKRW(n) {
  return new Intl.NumberFormat("ko-KR").format(n || 0);
}

export default function PartnerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goToNewDeal } = useDealRegistrationGate();
  const { profile } = usePartnerSession();

  // 필터·검색어는 URL 에 보관 — 상세에서 돌아와도 같은 목록·스크롤 위치로 복원
  const [filter, setFilter] = useQueryState("status", "all");
  const [search, setSearch] = useQueryState("q");
  const [companyDeals, setCompanyDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredNotice, setFeaturedNotice] = useState(null);

  // 우리 회사 딜 전체 — 서버가 partner_id 자동 필터 (JWT 기반)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listDeals()
      .then((items) => {
        if (!cancelled) setCompanyDeals(items);
      })
      .catch(() => {
        if (!cancelled) setCompanyDeals([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 대시보드 메인 공지 1건 — 없으면 배너 미표시
  useEffect(() => {
    let cancelled = false;
    fetchFeaturedNotice()
      .then((n) => {
        if (!cancelled) setFeaturedNotice(n || null);
      })
      .catch(() => {
        if (!cancelled) setFeaturedNotice(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleDeals = useMemo(() => {
    let list = companyDeals;
    // 목록의 상태 컬럼(DealStageBadge)과 **같은 함수**로 걸러낸다 — 배지가 '반려됨' 인 딜은
    // [반려] 탭에서, '심사 중입니다' 인 딜은 [심사] 탭에서 나와야 한다. d.status 로만
    // 걸러내면 승인 전 딜이 영업단계로 분류돼, 보이는 상태와 고를 수 있는 상태가 어긋난다.
    // (탭 라벨은 배지보다 짧다 — 문구가 아니라 **상태**가 일치하는 것이 기준이다. i18n 참고)
    if (filter !== "all") list = list.filter((d) => effectiveStatus(d) === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (d) =>
          d.projectName.toLowerCase().includes(q) ||
          d.customerCompany.toLowerCase().includes(q) ||
          d.salesRepName.toLowerCase().includes(q), // 등록자명 = 영업대표 이름
      );
    }
    return list;
  }, [companyDeals, filter, search]);

  const kpi = useMemo(() => {
    // 승인된 딜만 집계한다 — 심사 중·반려 딜을 '진행중' 에 넣으면 아직 인정되지 않은 건이
    // 확정된 파이프라인으로 보인다. 관리자 통계·실적도 서버에서 같은 기준으로 필터한다.
    // 대신 심사 대기 건수를 따로 세어 파트너가 자기 대기 물량을 알 수 있게 한다.
    const approved = companyDeals.filter(isReviewApproved);
    return {
      total: approved.length,
      inProgress: approved.filter((d) => IN_PROGRESS_STAGES.includes(d.status)).length,
      adoption: approved.filter((d) => d.status === "adoption").length,
      lost: approved.filter((d) => d.status === "lost").length,
      // '심사 중' 은 대기 건만 — 반려 딜은 삭제된 딜이라 등록 물량으로 세지 않는다
      underReview: companyDeals.filter(isReviewPending).length,
    };
  }, [companyDeals]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("partners.dashboard.greet", { company: profile.company, name: profile.name })}
              </h1>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/partners/notices" className="btw-btn-ghost">
                {t("nav.notices")}
              </Link>
              <Link to="/partners/edit" className="btw-btn-ghost">
                {t("partners.dashboard.editCompany")}
              </Link>
              <Link to="/partners/profile" className="btw-btn-ghost">
                {t("nav.profile")}
              </Link>
              <Link to="/partners/deals/search" className="btw-btn-ghost">
                {t("partners.dashboard.searchDeals")}
              </Link>
              <Link to="/partners/performance" className="btw-btn-ghost">
                {t("partners.dashboard.viewPerformance")}
              </Link>
              <Button variant="accent" onClick={goToNewDeal}>
                {t("partners.dashboard.addDeal")}
              </Button>
            </div>
          </header>

          {/* 메인 공지 — 관리자가 지정한 중요 공지 1건. 전체 딜 위 한 줄. */}
          {featuredNotice && (
            <button
              type="button"
              onClick={() => navigate(`/partners/notices/${featuredNotice.id}`)}
              className="mt-6 flex w-full items-center gap-3 rounded-2xl border border-accent/30 bg-accent/5 px-4 py-3 text-left transition hover:bg-accent/10"
            >
              <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-white">
                {t("partners.dashboard.featured.tag")}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                {featuredNotice.title}
              </span>
              <span className="shrink-0 text-xs font-medium text-accent">
                {t("partners.dashboard.featured.view")}
              </span>
            </button>
          )}

          {/* KPI — 승인된 딜 기준. 심사 대기가 있을 때만 다섯 번째 카드를 덧붙인다
              (평소엔 카드를 늘리지 않고, 대기 물량이 생기면 파트너가 바로 알게 한다) */}
          <section
            className={`mt-6 grid grid-cols-2 gap-3 ${kpi.underReview > 0 ? "md:grid-cols-5" : "md:grid-cols-4"}`}
          >
            <StatCard label={t("partners.dashboard.kpi.total")} value={kpi.total} />
            <StatCard label={t("partners.dashboard.kpi.inProgress")} value={kpi.inProgress} />
            <StatCard label={t("partners.dashboard.kpi.adoption")} value={kpi.adoption} />
            <StatCard label={t("partners.dashboard.kpi.lost")} value={kpi.lost} />
            {kpi.underReview > 0 && (
              <StatCard label={t("partners.dashboard.kpi.underReview")} value={kpi.underReview} />
            )}
          </section>

          {/* 필터 바 */}
          <section className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
            <Tabs
              value={filter}
              onChange={setFilter}
              // [심사] → 영업단계 7개 → [반려] (딜의 생애 순서, statusFilterOrder).
              // 라벨 조회가 축과 무관하게 한 형태로 통일되도록 filters.* 에 pending·rejected
              // 키를 함께 둔다 — 호출부에 축 분기를 만들지 않는다.
              items={[
                { key: "all", label: t("partners.dashboard.filters.all") },
                ...statusFilterOrder(SALES_STAGES).map((s) => ({
                  key: s,
                  label: t(`partners.dashboard.filters.${s}`),
                })),
              ]}
            />
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Input
                name="search"
                placeholder={t("partners.dashboard.filters.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-60"
              />
            </div>
          </section>

          {/* 테이블 / 카드 */}
          <section className="mt-4">
            {loading ? (
              <p className="py-10 text-center text-sm text-slate-400">
                {t("partners.common.loading")}
              </p>
            ) : visibleDeals.length === 0 ? (
              <EmptyState
                title={t("partners.dashboard.empty")}
                cta={
                  <Button variant="accent" onClick={goToNewDeal}>
                    {t("partners.dashboard.addDeal")}
                  </Button>
                }
              />
            ) : (
              <>
                {/* 데스크탑 테이블 */}
                <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dashboard.table.projectName")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dashboard.table.customer")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dashboard.table.registeredBy")}
                        </th>
                        <th className="px-4 py-3 text-right font-medium">
                          {t("partners.dashboard.table.poAmount")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dashboard.table.poDate")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dashboard.table.status")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dashboard.table.createdAt")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleDeals.map((d) => (
                        <tr
                          key={d.id}
                          onClick={() => navigate(`/partners/deals/${d.id}`)}
                          className="cursor-pointer transition hover:bg-slate-50"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {/* 내가 담당하는 딜 표시 — id 는 응답(문자열)과 세션(숫자일 수 있음)의 타입이
                                  달라 === 로는 항상 꺼져 있었다. 같은 회사 딜 중 내 딜 구분이 필요해 문자열로 비교.
                                  관리자 담당자 변경 후에는 등록 계정과 담당 계정이 다를 수 있어 담당 계정 기준 */}
                              {String(d.ownerUserId || d.createdByUserId) ===
                                String(profile.userId) && (
                                <span
                                  aria-hidden="true"
                                  className="inline-block h-4 w-0.5 rounded-full bg-accent"
                                />
                              )}
                              <span className="font-medium text-slate-900">{d.projectName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{d.customerCompany}</td>
                          <td className="px-4 py-3 text-slate-600">{d.salesRepName}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {formatKRW(d.poAmount)} {t("common.unitKrw")}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {formatQuarter(d.poExpectedQuarter) || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <DealStageBadge deal={d} />
                          </td>
                          <td className="px-4 py-3 text-slate-500">{d.createdAt?.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 모바일 카드 */}
                <div className="grid gap-2 md:hidden">
                  {visibleDeals.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => navigate(`/partners/deals/${d.id}`)}
                      className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-accent/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-900">{d.projectName}</span>
                        <DealStageBadge deal={d} />
                      </div>
                      <div className="text-xs text-slate-600">{d.customerCompany}</div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{d.salesRepName}</span>
                        <span className="tabular-nums">
                          {formatKRW(d.poAmount)} {t("common.unitKrw")}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

function Tabs({ value, onChange, items }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={() => onChange(it.key)}
          className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
            value === it.key
              ? "bg-slate-900 text-white"
              : "bg-white text-slate-600 hover:bg-slate-100"
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}
